import { useState } from 'react';

const SAPStaffAugmentation = () => {
  // Color constants with hex codes
  const colors = {
    primary: "#FF6B35",     // Deep blue
    secondary: "black",   // Bright blue
    accent: "#00377A",      // Dark blue
    light: "#E6F0FF",       // Light blue
    highlight: "#4C9AFF",   // Medium blue
    text: "#172B4D",        // Dark text
    lightText: "#5E6C84",   // Light text
    background: "#FFFFFF"   // White background
  };

  // Services data with detailed points that appear on hover
  const services = [
    {
      title: "Certified SAP Consultants",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/SAP%20what%20we%20offer/5.png?updatedAt=1754031458144",
      details: [
        "SAP Certified professionals with 5-15+ years experience",
        "Specialized in both functional and technical domains",
        "Regular certification updates and continuous learning"
      ]
    },
    {
      title: "Module Expertise",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/SAP%20what%20we%20offer/4.png?updatedAt=1754031458123",
      details: [
        "SAP S/4HANA and ECC specialists",
        "Expertise in FICO, MM, SD, PP, HCM modules",
        "Advanced skills in ABAP, BASIS, and integrations"
      ]
    },
    {
      title: "Flexible Engagement Models",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/SAP%20what%20we%20offer/7.png?updatedAt=1754031458109",
      details: [
        "Short-term project-based engagements",
        "Long-term staff augmentation options",
        "Seamless scaling based on project phases"
      ]
    },
    {
      title: "Global Talent Pool",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/SAP%20what%20we%20offer/10.png?updatedAt=1754031458188",
      details: [
        "On-site professionals in major business hubs",
        "Offshore delivery centers for cost optimization",
        "Hybrid teams with 24/7 coverage capabilities"
      ]
    },
    {
      title: "Quick Turnaround",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/SAP%20what%20we%20offer/5.png?updatedAt=1754031458144",
      details: [
        "Rapid resource allocation within 1-2 weeks",
        "Immediate availability for urgent requirements",
        "Streamlined onboarding processes"
      ]
    },
    {
      title: "Cost-Effective Solutions",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/SAP%20what%20we%20offer/9.png?updatedAt=1754031458167",
      details: [
        "Optimized pricing through blended delivery models",
        "Flexible pricing based on engagement duration",
        "Value-based delivery without quality compromise"
      ]
    }
  ];

  return (
    <div className="w-full p-4 md:p-8 lg:p-12 min-h-screen" style={{ background: colors.background, color: colors.text }}>
      {/* Header Section with consistent blue styling */}
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-3xl lg:text-3xl font-bold mb-4 text-center" style={{ color: colors.primary }}>
          SAP Staff Augmentation & Staffing Services
        </h2>
        <p className="text-xl md:text-xl max-w-xl mx-auto text-center" style={{ color: colors.secondary }}>
          Scale Your SAP Projects with Expert Talent — On Demand
        </p>
      </div>

      {/* What We Offer Section */}
      <h3 className="text-2xl md:text-3xl font-bold mb-10 text-center" style={{ color: colors.accent }}>
        What We Offer
      </h3>

      {/* Services Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {services.map((service, index) => (
          <ServiceCard key={index} service={service} colors={colors} />
        ))}
      </div>
    </div>
  );
}

// Individual service card component with hover effect
function ServiceCard({ service, colors }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="relative overflow-hidden rounded-lg h-64 transform transition-all duration-300 hover:shadow-lg border"
      style={{ 
        background: colors.background,
        borderColor: colors.light,
        boxShadow: isHovered ? `0 10px 15px -3px rgba(0, 82, 204, 0.1), 0 4px 6px -2px rgba(0, 82, 204, 0.05)` : 'none'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
    >
      {/* Default View (Image + Title) */}
      <div className={`absolute inset-0 p-6 flex flex-col transition-all duration-300 ${isHovered ? 'opacity-0 transform translate-y-full' : 'opacity-100 transform translate-y-0'}`}>
        {/* Image */}
        <div className="h-36 mb-4 overflow-hidden rounded-md">
          <img 
            src={service.imageUrl} 
            alt={service.title} 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Title */}
        <h4 className="text-lg font-semibold" style={{ color: "#172B4D" }}>
          {service.title}
        </h4>
      </div>
      
      {/* Hover View (Details) */}
      <div className={`absolute inset-0 p-6 flex flex-col transition-all duration-300 ${isHovered ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-full'}`}>
        {/* Title */}
        <h4 className="text-lg font-semibold mb-4" style={{ color: colors.primary }}>
          {service.title}
        </h4>
        
        {/* Details */}
        <ul className="space-y-3">
          {service.details.map((detail, idx) => (
            <li key={idx} className="flex items-start text-sm">
              <span className="inline-block w-2 h-2 mt-1.5 mr-2 rounded-full flex-shrink-0" style={{ background: colors.primary }}></span>
              <span style={{ color: colors.lightText }}>{detail}</span>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Bottom border */}
      <div 
        className={`absolute bottom-0 left-0 w-full h-1 transition-all duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: colors.primary }}>
      </div>
    </div>
  );
}

export default SAPStaffAugmentation